/**
 * MyMemberships — `/my/memberships`. Member self-service dashboard.
 *
 * For every active or scheduled-cancel membership the logged-in user
 * holds, we render a card with:
 *   • brand banner + plan summary + next billing / passport balance
 *   • upcoming brand events grid with one-click "Reserve free spot"
 *     (auto-issues a free ticket via the existing RSVP endpoint)
 *   • cancel-at-period-end for subscriptions (Spotify model)
 *
 * Empty state nudges the user toward `/series` directory.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, Calendar, Ticket, CheckCircle2, Sparkles, AlertTriangle,
  ArrowRight, XCircle, Clock, Trophy, ExternalLink,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatEventDate(dateStr, timeStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  const base = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return timeStr ? `${base} · ${timeStr}` : base;
}

function formatPeriodEnd(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  if (status === 'active' || status === 'trialing') {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]" data-testid="status-active">
        <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Active
      </Badge>
    );
  }
  if (status === 'scheduled_cancel') {
    return (
      <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]" data-testid="status-scheduled">
        <Clock className="h-2.5 w-2.5 mr-1" /> Ends at period
      </Badge>
    );
  }
  if (status === 'past_due') {
    return (
      <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 text-[10px]" data-testid="status-past-due">
        <AlertTriangle className="h-2.5 w-2.5 mr-1" /> Payment failed
      </Badge>
    );
  }
  return <Badge variant="outline" className="text-[10px] capitalize">{status}</Badge>;
}

function MembershipCard({ membership, onChanged }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [busyEvent, setBusyEvent] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const brand = membership.brand || {};
  const accent = brand.accent_color || '#8b5cf6';
  const isSubscription = membership.pricing_model === 'subscription';
  const passportRemaining = (isSubscription || !membership.event_limit)
    ? null
    : Math.max(0, (membership.event_limit || 0) - (membership.events_consumed || 0));

  const rsvp = async (ev) => {
    setBusyEvent(ev.event_id);
    try {
      const res = await axios.post(
        `${API}/memberships/${membership.membership_id}/rsvp/${ev.event_id}`,
        {}, { headers },
      );
      toast.success(res.data?.already ? 'You already have a ticket for this event' : 'Reserved! Confirmation on the way.');
      onChanged?.();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not reserve spot';
      // Common case: plan only has discount, not free admission — point them at the regular checkout.
      if (msg.toLowerCase().includes('free admission')) {
        toast.info('This plan unlocks a discount — buy tickets normally and it auto-applies at checkout.');
      } else {
        toast.error(msg);
      }
    } finally {
      setBusyEvent(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this subscription? You keep access until the end of the current period.')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/memberships/${membership.membership_id}/cancel`, {}, { headers });
      toast.success('Subscription will end at period close.');
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Card className="bg-card border-border overflow-hidden" data-testid={`my-membership-${membership.membership_id}`}>
      {/* Brand header */}
      <div
        className="px-5 py-4 border-b border-border"
        style={brand.hero_image_url ? {
          backgroundImage: `linear-gradient(rgba(10,10,20,0.78), rgba(10,10,20,0.92)), url(${brand.hero_image_url})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
        } : { background: `linear-gradient(135deg, ${accent}22, transparent)` }}
      >
        <div className="flex items-start gap-3">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt="" className="h-12 w-12 rounded-md border border-border bg-background object-cover shrink-0" />
          ) : (
            <div className="h-12 w-12 rounded-md flex items-center justify-center shrink-0" style={{ background: `${accent}22` }}>
              <Trophy className="h-5 w-5" style={{ color: accent }} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <Link to={`/series/${brand.slug}`} className="font-bold text-foreground text-lg hover:text-indigo-300 transition-colors inline-flex items-center gap-1.5" data-testid={`member-brand-link-${membership.brand_id}`}>
              {brand.name || 'Unknown brand'} <ExternalLink className="h-3 w-3 opacity-60" />
            </Link>
            <div className="flex items-center gap-2 flex-wrap mt-1">
              <span className="text-xs text-foreground/70 capitalize">
                {isSubscription
                  ? `${membership.billing_interval}ly subscription · $${membership.price}/${membership.billing_interval}`
                  : `Passport · $${membership.price}`}
              </span>
              <StatusBadge status={membership.status} />
            </div>
            {isSubscription && membership.current_period_end && (
              <p className="text-[11px] text-muted-foreground mt-1">
                {membership.cancel_at_period_end
                  ? `Access ends ${formatPeriodEnd(membership.current_period_end)}`
                  : `Renews ${formatPeriodEnd(membership.current_period_end)}`}
              </p>
            )}
            {passportRemaining !== null && (
              <p className="text-[11px] text-muted-foreground mt-1" data-testid={`passport-balance-${membership.membership_id}`}>
                <span className="text-foreground font-semibold">{passportRemaining}</span> of {membership.event_limit} event{membership.event_limit === 1 ? '' : 's'} remaining
              </p>
            )}
          </div>
          {isSubscription && membership.status === 'active' && !membership.cancel_at_period_end && (
            <Button
              size="sm"
              variant="ghost"
              className="text-rose-300 hover:text-rose-200 hover:bg-rose-500/10 shrink-0"
              onClick={cancel}
              disabled={cancelling}
              data-testid={`cancel-membership-${membership.membership_id}`}
            >
              {cancelling ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1.5" />}
              Cancel
            </Button>
          )}
        </div>
      </div>

      {/* Upcoming brand events */}
      <CardContent className="p-5">
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5" />
          Upcoming brand events {membership.upcoming_events?.length > 0 && `· ${membership.upcoming_events.length}`}
        </h4>

        {!membership.upcoming_events?.length ? (
          <div className="text-sm text-muted-foreground py-4 text-center">
            No upcoming events yet — check back soon, or follow{' '}
            <Link to={`/series/${brand.slug}`} className="text-indigo-300 hover:text-indigo-200">/series/{brand.slug}</Link>.
          </div>
        ) : (
          <div className="space-y-2" data-testid={`member-events-${membership.membership_id}`}>
            {membership.upcoming_events.map((ev) => {
              const passportExhausted = passportRemaining === 0 && !ev.claimed;
              const isBusy = busyEvent === ev.event_id;
              return (
                <div
                  key={ev.event_id}
                  className="flex items-center gap-3 p-3 rounded-md border border-border bg-background/40"
                  data-testid={`member-event-row-${ev.event_id}`}
                >
                  {ev.image_url && (
                    <img src={ev.image_url} alt="" className="h-12 w-12 rounded-md object-cover shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-300/90">
                      {formatEventDate(ev.event_date, ev.event_time)}
                    </div>
                    <Link
                      to={`/events/${(ev.city || '').toLowerCase()}/${ev.slug || ev.event_id}`}
                      className="text-sm font-semibold text-foreground hover:text-indigo-300 transition-colors line-clamp-1"
                    >
                      {ev.name}
                    </Link>
                    {(ev.venue_name || ev.venue_city) && (
                      <p className="text-[11px] text-muted-foreground line-clamp-1">
                        {ev.venue_name || ev.venue_city}
                      </p>
                    )}
                  </div>
                  {ev.claimed ? (
                    <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] shrink-0" data-testid={`event-claimed-${ev.event_id}`}>
                      <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> Reserved
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => rsvp(ev)}
                      disabled={isBusy || passportExhausted}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0"
                      data-testid={`reserve-spot-${ev.event_id}`}
                    >
                      {isBusy
                        ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        : <Ticket className="h-3.5 w-3.5 mr-1.5" />}
                      {passportExhausted ? 'Limit reached' : 'Reserve free spot'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function MyMemberships() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await axios.get(`${API}/memberships/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMemberships(res.data?.memberships || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load memberships');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-7 w-7 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-12" data-testid="my-memberships-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="h-3.5 w-3.5" /> Memberships
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mt-1">My memberships</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {user?.email && <>Signed in as <span className="text-foreground">{user.email}</span></>}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/series')}
            data-testid="browse-series-btn"
          >
            Browse series <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </div>

        {memberships.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="p-10 text-center">
              <div className="h-12 w-12 rounded-full bg-indigo-500/15 flex items-center justify-center mx-auto mb-3">
                <Sparkles className="h-5 w-5 text-indigo-300" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">No memberships yet</h3>
              <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-md mx-auto">
                Join a club or grab a multi-event passport from your favorite series — one purchase, every event.
              </p>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => navigate('/series')}
                data-testid="empty-browse-btn"
              >
                Browse series <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="my-memberships-list">
            {memberships.map((m) => (
              <MembershipCard key={m.membership_id} membership={m} onChanged={load} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
